import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TableSortLabel,
  Tooltip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from '@mui/material';
import {
  FileDownload as ExportIcon,
  Assessment as ReportIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Event as EventIcon,
  Business as VenueIcon,
  Schedule as ScheduleIcon,
  InfoOutlined as InfoOutlinedIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// [Web UX 9 / T2, 2026-10-13] a dátumválasztók magyar feliratai (Cancel/OK/Select date helyett) — lásd utils/dateLocale.ts
import { HU_PICKER_LOCALE_TEXT } from '../utils/dateLocale';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import { apiErrorMessage } from '../utils/apiErrors';
/**
 * PÉNZ-KIÍRÁS EGY KÖZÖS SEGÉDEN [B-W3, 2026-09-16] — `utils/money.ts`.
 *
 * MI VOLT A BAJ EZEN A KÉPERNYŐN:
 *  1. `user.totalCollected ? `${…} Ft` : '-'` — a 0 hamis, tehát a „nem szedett be semmit" és a
 *     „nincs erről adatunk" UGYANAZT a „–"-t adta. Pénzügyi kimutatásban ez két különböző
 *     állítás; a klub nem tudta megmondani, melyikről van szó.
 *  2. Az összegek tagolás nélkül, egyben jelentek meg („2586900 Ft") — hét számjegyet ránézésre
 *     senki nem olvas el helyesen, és épp ezekből lesz utalás.
 * A segéd fejléce (`utils/money.ts`) a szóköz-karakter normalizálását is megindokolja.
 */
import { NO_AMOUNT_TEXT, formatForint } from '../utils/money';
import {
  MultiReportSort,
  MultiReportSortKey,
  defaultSortDirection,
  filterMultiUserReportByName,
  sortMultiUserReport,
} from '../utils/reportSort';
import { reportService } from '../services/reportService';
import { userService } from '../services/userService';
import { calendarService } from '../services/calendarService';
import { UserListItem } from '../types/user';
import { Venue } from '../types/calendar';
import {
  ParticipationReport,
  ReportSummary,
  MultiUserReport,
  AggregatedSummary,
  VenueOccupancyReport,
  TableOccupancyReport,
} from '../types/report';

/**
 * A DÁTUMVÁLASZTÓK KORLÁTJA — SZÁNDÉKOSAN A SZERVER KORLÁTJÁVAL EGYEZIK [2026-09-15].
 *
 * A riport-végpontok az évet `[Range(2000, 2100)]`-nal validálják
 * (`backend/PinyoApi/Controllers/ReportsController.cs:114` → `YearOutOfRangeMessage`).
 * A választókon eddig NEM volt `minDate`/`maxDate`, tehát a felület engedett olyan évet (pl.
 * 1999), amire a szerver garantáltan 400-at ad. Egy helyen definiáljuk, hogy a két korlát ne
 * tudjon szétcsúszni; ha a szerver `Range`-e változik, EZT is át kell vezetni.
 */
const REPORT_MIN_DATE = dayjs('2000-01-01');
const REPORT_MAX_DATE = dayjs('2100-12-31');

/**
 * A TARTOMÁNYON KÍVÜLI DÁTUM VISSZAVÁGÁSA [S1, 2026-09-15, review-lelet].
 *
 * MI VOLT A BAJ: a `minDate`/`maxDate` a NAPTÁR-RÁCSOT korlátozza, de a MUI szövegmezőjébe
 * kézzel BEGÉPELT évet (pl. „1999") az `onChange` akkor is megkapja — a `if (date)` ág pedig
 * kérdés nélkül beállította. A kérés így 400-cal jött vissza, vagyis a lelet („a felület ne
 * engedjen olyat, amit a szerver elutasít") a gépelt úton NYITVA maradt.
 *
 * A DÖNTÉS: KLAMPELÉS, nem elutasítás. Elutasítással a mező „beragadna" gépelés közben (a
 * részleges „19" is érvénytelen évnek számít), a klampelés viszont a legközelebbi ENGEDETT
 * dátumra visz — a felhasználó látja, hogy hol a határ. Érvénytelen (nem értelmezhető) dátumot
 * továbbra sem állítunk be.
 */
const clampToReportRange = (date: unknown): Dayjs | null => {
  if (!date || !dayjs.isDayjs(date) || !date.isValid()) return null;
  if (date.isBefore(REPORT_MIN_DATE)) return REPORT_MIN_DATE;
  if (date.isAfter(REPORT_MAX_DATE)) return REPORT_MAX_DATE;
  return date;
};

/**
 * AZ ARÁNYOK (i) MAGYARÁZATA — A MAI SZERVER-SZABÁLYBÓL KIOLVASVA [Web UX 3. csomag (C), 2026-10-04]
 *
 * MI VOLT A BAJ (`Documents/WEB_UX_JAVASLATOK_2026-10-02.md` 1.4): a „Részvételi arány" és a
 * „Fizetési arány" mellett semmi nem mondta meg, MIHEZ mérünk — pedig a két arány nevezője
 * KÜLÖNBÖZIK, és a bázis egyszer már némán elcsúszott (B-M9, lásd a fizetési-arány kártyánál).
 * A szövegek a szerver KÓDJÁBÓL vannak kiolvasva, nem a jelentésből (a jelentés „az összes
 * esemény"-t írt, ami pontatlan):
 *  • JÁTÉKOS, részvétel: `ReportsController.cs:538-540` (egy tag) és `:1319-1322` (mindenki) —
 *    `totalEvents` = a tag jelenléti SORAI az aktív (nem törölt) eseményeken, `attendedEvents` =
 *    ebből az `IsPresent`. NEM a klub összes eseménye.
 *  • JÁTÉKOS, fizetési arány: `:643-645` és `:1345` — (aktív, jelen ÉS helyszínen fizetett) / jelen.
 *  • JÁTÉKOS, „Fizetett" darab: `:543` és `:1324` — MINDEN `HasPaidOnSite` sor, az utólag törölt
 *    eseményen fizetett is (a CSV-fejléc is így mondja: „Fizetett (db, a törölttel EGYÜTT)").
 *  • EDZŐ, részvétel — MÁS FORRÁS: `:746` + `:809-810` + `:854` (egy edző), `:1509` + `:1575` +
 *    `:1599` + `:1610` (mindenki): `totalEvents` = azok az aktív események, amelyekre EDZŐKÉNT
 *    be van osztva (UserEvents, edző-szerep), `attendedEvents` = ezek közül, amelyeken a SAJÁT
 *    jelenléti sora „jelen" (`ea.IsPresent == true`, `:793`, `:1545`). Edzőnél nincs fizetési arány.
 * ⚠️ A szöveget a klub még NEM hagyta jóvá — a WEB_UX dokumentumban „ideiglenes" jelöléssel áll.
 * Ha a szerver szabálya változik, EZT is át kell vezetni (a `Reports.sort.test.tsx` a szöveget
 * szó szerint rögzíti).
 */
export const RATE_EXPLANATIONS = {
  playerParticipation:
    'Részvételi arány: az időszak aktív (nem törölt) eseményei közül, amelyeken a tag a jelenléti íven szerepel, hányon volt jelen.',
  coachParticipation:
    'Részvételi arány (edző): az időszak aktív (nem törölt) eseményei közül, amelyekre edzőként be van osztva, hányon van a jelenléti íven „jelen” jelölése.',
  playerPayment:
    'Fizetési arány: azokból az alkalmakból, amikor jelen volt, hányszor fizetett a helyszínen (csak aktív eseményen).',
  playerPaidCount:
    'Fizetett: hány alkalommal fizetett a helyszínen (darab, nem forint) — az utólag törölt eseményen fizetett alkalmakkal együtt.',
} as const;

/**
 * RÖGZÍTETT NÉVOSZLOP [Web UX 3. csomag (C), 2026-10-04]: az edzői táblázat 14 oszlopa
 * vízszintesen görget, és a „Hátralék" oszlopnál már nem látszott, KINEK a sora.
 * A háttér a téma PAPÍR-színe (`background.paper`) ÉS a sötét téma Paper-rétege
 * (`--Paper-overlay`: a MUI a sötét témában a kártyára egy világosító gradienst tesz, amit a
 * leszármazott elemek CSS-változóként örökölnek) — enélkül a rögzített cella sötét témában
 * egy árnyalattal sötétebb lenne a táblánál, világos témában a változó nincs megadva → nincs kép.
 */
const STICKY_NAME_CELL_SX = {
  position: 'sticky',
  left: 0,
  zIndex: 1,
  bgcolor: 'background.paper',
  backgroundImage: 'var(--Paper-overlay)',
} as const;
const STICKY_NAME_HEAD_SX = { ...STICKY_NAME_CELL_SX, zIndex: 3 } as const;

/** Az (i) jel: egérrel ráállva / fókuszra a `Tooltip`, felolvasóval az `aria-label` mondja ki. */
const InfoHint: React.FC<{ text: string; testId?: string }> = ({ text, testId }) => (
  <Tooltip title={text} arrow>
    <IconButton size="small" aria-label={text} data-testid={testId} sx={{ ml: 0.25, p: 0.25 }}>
      <InfoOutlinedIcon fontSize="inherit" />
    </IconButton>
  </Tooltip>
);

const Reports: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState(dayjs().startOf('month'));
  const [reportData, setReportData] = useState<ParticipationReport[]>([]);
  const [reportSummary, setReportSummary] = useState<ReportSummary | null>(null);
  const [reportType, setReportType] = useState<'player' | 'coach' | 'venue' | 'table'>('player');
  const [reportMode, setReportMode] = useState<'single' | 'multi'>('single');
  const [multiUserReportData, setMultiUserReportData] = useState<MultiUserReport[]>([]);
  const [aggregatedSummary, setAggregatedSummary] = useState<AggregatedSummary | null>(null);
  const [venueOccupancyData, setVenueOccupancyData] = useState<VenueOccupancyReport | null>(null);
  const [tableOccupancyData, setTableOccupancyData] = useState<TableOccupancyReport | null>(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedVenueId, setSelectedVenueId] = useState<string>('');
  const [venues, setVenues] = useState<Venue[]>([]);
  const [startDate, setStartDate] = useState(dayjs().subtract(1, 'month'));
  const [endDate, setEndDate] = useState(dayjs());
  const [dateFilterMode, setDateFilterMode] = useState<'monthly' | 'range'>('monthly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  // [Web UX 3. csomag (C), 2026-10-04] A többfős táblázat rendezése és névszűrője — CSAK
  // megjelenítés (a betöltött listán, kliens-oldalon); `null` = a szerver sorrendje.
  const [multiSort, setMultiSort] = useState<MultiReportSort | null>(null);
  const [multiNameFilter, setMultiNameFilter] = useState('');

  const displayedMultiUsers = useMemo(
    () => sortMultiUserReport(filterMultiUserReportByName(multiUserReportData, multiNameFilter), multiSort),
    [multiUserReportData, multiNameFilter, multiSort],
  );

  const handleMultiSort = (key: MultiReportSortKey) => {
    setMultiSort((prev) =>
      prev && prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: defaultSortDirection(key) },
    );
  };

  /** Egy rendezhető fejléc-cella; a látható felirat VÁLTOZATLAN (E2E / Jest pontos szövegre keres). */
  const sortHeader = (
    key: MultiReportSortKey,
    label: string,
    opts: { align?: 'left' | 'center'; sticky?: boolean; info?: string; infoTestId?: string } = {},
  ) => {
    const active = multiSort?.key === key;
    const direction = active && multiSort ? multiSort.direction : defaultSortDirection(key);
    return (
      <TableCell
        align={opts.align ?? 'center'}
        sortDirection={active ? direction : false}
        sx={opts.sticky ? STICKY_NAME_HEAD_SX : undefined}
      >
        <TableSortLabel
          active={active}
          direction={direction}
          onClick={() => handleMultiSort(key)}
          data-testid={`reports-sort-${key}`}
        >
          {label}
        </TableSortLabel>
        {opts.info && <InfoHint text={opts.info} testId={opts.infoTestId} />}
      </TableCell>
    );
  };

  /**
   * „ELŐZŐ HÓNAP" GYORSGOMB [Web UX 3. csomag (C), 2026-10-04] — a jelentés 1.4 pontja.
   * A MAI naptári hónaphoz képest előző hónap (nem a kiválasztotthoz: a gomb így mindig
   * ugyanazt jelenti, és kétszer megnyomva sem csúszik tovább). Havi módra vált (az „Időszak"
   * módban a hónap-választó nem is látszik), és a kezdő/befejező dátumot is az előző hónap
   * ELSŐ és UTOLSÓ napjára állítja — a `dayjs` HELYI naptárával, tehát `format('YYYY-MM-DD')`
   * után sem csúszik UTC-be. Az alapértelmezett időszak (folyó hónap, fent) NEM változik.
   *
   * EGY KATTINTÁS = AZ ELŐZŐ HÓNAP KIMUTATÁSA [1. validátor-kör M7, 2026-10-04]: korábban a gomb
   * csak a szűrőt állította, a lekérdezést a „Kimutatás betöltése" indította. Közben a táblázat a
   * RÉGI hónapot mutatta, a „CSV exportálás" viszont (az ÁLLAPOTBÓL olvasva) már az ÚJ hónapot
   * töltötte volna le — a képernyő és a fájl eltért. Most a szűrő átállítása UTÁN a kimutatás be is
   * töltődik, így a táblázat, a CSV és a szűrő egyezik.
   * HOGYAN: a `loadReport` a render-zárlatból olvassa a szűrőket, ezért közvetlenül a kattintásban
   * hívva még a RÉGI hónapra kérne. Egy számláló (`previousMonthLoadTick`) lép, és a rá figyelő
   * effekt az ÚJ állapotú renderben hívja a `loadReport`-ot — ugyanazt az utat, mint a gomb (minden
   * őrrel: egy-felhasználós módban kiválasztott felhasználó nélkül nem kér).
   */
  /**
   * ELAVULT VÁLASZ ELDOBÁSA [Web UX 3. csomag (C), 2026-10-04 — 2. validátor-kör, F2].
   *
   * MI VOLT A BAJ (mérve, Chrome): a „Játékos részvétel" kimutatás betöltése közben „Edző
   * részvétel"-re váltva a típusváltó kiürítette a táblázatot, de a MÉG ÚTON LÉVŐ játékos-válasz
   * utána megérkezett és beíródott — a játékosok sorai az „Edzők összesített kimutatása"
   * fejléc és az edzői oszlopok alatt jelentek meg. Két egymás utáni betöltésnél ugyanígy a
   * LASSABB (korábbi) válasz írhatta felül a frissebbet.
   *
   * HOGYAN: minden betöltés sorszámot kap (`loadSeqRef`, minta: `UserManagement.tsx`); a válasz
   * — adat, hiba ÉS a `loading` visszaállítása is — csak akkor íródik be, ha közben nem indult
   * újabb betöltés, és a szűrő sem változott (`isStale`).
   *
   * DÖNTÉS — ürítés + sorszám, NEM „az adat a típusával együtt tárolva": a fejléc és az oszlopok
   * ~100 helyen a `reportType`-ból renderelnek; a betöltött típus külön tárolása mindegyik ág
   * átírását kívánná. Ehelyett a szabály: a táblázatban SOHA nem maradhat más szűrőhöz tartozó
   * adat. Ezért BÁRMELY lekérdezési feltétel (típus, mód, hónap, dátumszűrés, kezdő/záró nap,
   * felhasználó, helyszín, nap) változásakor az érvényes adat ürül, a sorszám lép (az úton lévő
   * válasz eldobódik), és a töltésjelző leáll. Ez az 1. kör M7-es esetét (a képernyő a régi
   * hónapot mutatja, a CSV már az újat töltené le) is általánosan zárja.
   * ⚠️ Ennek az effektnek a `previousMonthLoadTick`-effekt ELŐTT kell állnia: a React a
   * deklaráció sorrendjében futtatja őket, így az „Előző hónap" gomb először érvénytelenít,
   * és CSAK UTÁNA indítja az új betöltést (fordítva a saját betöltését dobná el).
   */
  const loadSeqRef = useRef(0);
  const clearReportData = () => {
    setReportData([]);
    setReportSummary(null);
    setMultiUserReportData([]);
    setAggregatedSummary(null);
    setVenueOccupancyData(null);
    setTableOccupancyData(null);
  };
  useEffect(() => {
    loadSeqRef.current += 1;
    clearReportData();
    setLoading(false);
  }, [reportType, reportMode, selectedMonth, dateFilterMode, startDate, endDate, selectedUser, selectedVenueId, selectedDate]);

  const [previousMonthLoadTick, setPreviousMonthLoadTick] = useState(0);
  const selectPreviousMonth = () => {
    const prev = dayjs().startOf('month').subtract(1, 'month');
    setSelectedMonth(prev);
    setStartDate(prev);
    setEndDate(prev.endOf('month').startOf('day'));
    setDateFilterMode('monthly');
    setPreviousMonthLoadTick((n) => n + 1);
  };
  useEffect(() => {
    if (previousMonthLoadTick > 0) loadReport();
  }, [previousMonthLoadTick]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadUsers();
    loadVenues();
  }, []);

  const loadUsers = async () => {
    try {
      const result = await userService.getUsers();
      // Sort users alphabetically by name
      const sortedUsers = result.data
        .filter(user => user.isActive)
        .sort((a, b) => a.name.localeCompare(b.name, 'hu'));
      setUsers(sortedUsers);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const loadVenues = async () => {
    try {
      const venueData = await calendarService.getActiveVenues();
      // Sort venues alphabetically by name
      const sortedVenues = venueData.sort((a, b) => a.name.localeCompare(b.name, 'hu'));
      setVenues(sortedVenues);
    } catch (err) {
      console.error('Error loading venues:', err);
    }
  };

  const loadReport = async () => {
    // Handle table occupancy report
    if (reportType === 'table') {
      if (!selectedDate || !selectedVenueId) return;
      const seq = ++loadSeqRef.current;
      const isStale = () => seq !== loadSeqRef.current;

      try {
        setLoading(true);
        setError('');

        const data = await reportService.getTableOccupancyReport(
          selectedDate.format('YYYY-MM-DD'),
          selectedVenueId
        );
        if (isStale()) return;

        setTableOccupancyData(data);
        // Clear other report data
        setReportData([]);
        setReportSummary(null);
        setMultiUserReportData([]);
        setAggregatedSummary(null);
        setVenueOccupancyData(null);
      } catch (err: any) {
        if (isStale()) return;
        console.error('Table occupancy report error:', err);
        // [Web UX 3. csomag (C), 2026-10-04] Közös hibaszöveg-minta (`utils/apiErrors.ts`): a
        // burkolt magyar indoklás is kijut, az angol axios-mondat („Request failed with status
        // code 500", „Network Error") SOHA — az axios-hiba saját szövege többé nem tartalék.
        setError(apiErrorMessage(err, 'Hiba történt az asztal kimutatás betöltésekor'));
        setTableOccupancyData(null);
      } finally {
        if (!isStale()) setLoading(false);
      }
      return;
    }

    // Handle venue occupancy report differently
    if (reportType === 'venue') {
      if (!startDate || !endDate) return;
      const seq = ++loadSeqRef.current;
      const isStale = () => seq !== loadSeqRef.current;

      try {
        setLoading(true);
        setError('');

        const data = await reportService.getVenueOccupancyReport(
          startDate.format('YYYY-MM-DD'),
          endDate.format('YYYY-MM-DD')
        );
        if (isStale()) return;

        setVenueOccupancyData(data);
        // Clear other report data
        setReportData([]);
        setReportSummary(null);
        setMultiUserReportData([]);
        setAggregatedSummary(null);
      } catch (err: any) {
        if (isStale()) return;
        console.error('Venue occupancy report error:', err);
        // [Web UX 3. csomag (C), 2026-10-04] Közös hibaszöveg-minta — lásd az asztal-ágat.
        setError(apiErrorMessage(err, 'Hiba történt a helyszín kimutatás betöltésekor'));
        setVenueOccupancyData(null);
      } finally {
        if (!isStale()) setLoading(false);
      }
      return;
    }

    // Single user mode requires selected user, multi user mode doesn't
    if (reportMode === 'single' && !selectedUser) return;
    if (!selectedMonth) return;
    const seq = ++loadSeqRef.current;
    const isStale = () => seq !== loadSeqRef.current;

    try {
      setLoading(true);
      setError('');

      const year = selectedMonth.year();
      const month = selectedMonth.month() + 1;

      if (reportMode === 'single') {
        // Original single user functionality
        if (reportType === 'player') {
          const data = await reportService.getPlayerParticipationReport(selectedUser, year, month);
          if (isStale()) return;
          // Validate response data structure
          if (!data || typeof data !== 'object') {
            throw new Error('Érvénytelen válasz az API-tól');
          }
          setReportData(Array.isArray(data.events) ? data.events : []);
          setReportSummary(data.summary || { totalEvents: 0, attendedEvents: 0, eventTypeBreakdown: [] });
        } else {
          // For coach reports, use date range if enabled
          let startDateStr, endDateStr;
          if (dateFilterMode === 'range' && reportType === 'coach') {
            startDateStr = startDate.format('YYYY-MM-DD');
            endDateStr = endDate.format('YYYY-MM-DD');
          }

          const data = await reportService.getCoachParticipationReport(selectedUser, year, month, startDateStr, endDateStr);
          if (isStale()) return;
          // Validate response data structure
          if (!data || typeof data !== 'object') {
            throw new Error('Érvénytelen válasz az API-tól');
          }
          setReportData(Array.isArray(data.events) ? data.events : []);
          setReportSummary(data.summary || { totalEvents: 0, attendedEvents: 0, eventTypeBreakdown: [] });
        }
        // Clear multi-user data
        setMultiUserReportData([]);
        setAggregatedSummary(null);
      } else {
        // New multi-user functionality
        if (reportType === 'player') {
          const data = await reportService.getAllPlayersParticipationReport(year, month);
          if (isStale()) return;
          // Validate response data structure
          if (!data || typeof data !== 'object') {
            throw new Error('Érvénytelen válasz az API-tól');
          }
          setMultiUserReportData(Array.isArray(data.users) ? data.users : []);
          setAggregatedSummary(data.aggregatedSummary || { totalUsers: 0, totalEvents: 0 });
        } else {
          // For multi-user coach reports, use date range if enabled
          let startDateStr, endDateStr;
          if (dateFilterMode === 'range') {
            startDateStr = startDate.format('YYYY-MM-DD');
            endDateStr = endDate.format('YYYY-MM-DD');
          }

          const data = await reportService.getAllCoachesParticipationReport(year, month, startDateStr, endDateStr);
          if (isStale()) return;
          // Validate response data structure
          if (!data || typeof data !== 'object') {
            throw new Error('Érvénytelen válasz az API-tól');
          }
          setMultiUserReportData(Array.isArray(data.users) ? data.users : []);
          setAggregatedSummary(data.aggregatedSummary || { totalUsers: 0, totalEvents: 0 });
        }
        // Clear single-user data
        setReportData([]);
        setReportSummary(null);
      }
      // Clear venue data
      setVenueOccupancyData(null);
    } catch (err: any) {
      if (isStale()) return;
      console.error('Report loading error:', err);
      // [Web UX 3. csomag (C), 2026-10-04] Közös hibaszöveg-minta — lásd az asztal-ágat. A
      // saját „Érvénytelen válasz az API-tól" dobásunk (fent) nem axios-hiba, arra a magyar
      // tartalék megy ki.
      setError(apiErrorMessage(err, 'Hiba történt a kimutatás betöltésekor'));
      // Reset data on error to prevent stale state
      setReportData([]);
      setReportSummary(null);
      setMultiUserReportData([]);
      setAggregatedSummary(null);
      setVenueOccupancyData(null);
    } finally {
      if (!isStale()) setLoading(false);
    }
  };

  /**
   * Export-hiba felhasználói szövege [Web UX 3. csomag (C), 2026-10-04 — F1].
   * Az export a `reportService.downloadFile`-on át megy, ami a `responseType: 'blob'` miatti
   * Blob-törzsből MÁR kiolvasta a szerver magyar indoklását, és `userMessage`-ként adja tovább
   * (a „Nem sikerült exportálni…" tartalék, a HTTP-kód és a hálózati mondat is ott áll elő).
   * Ha valamiért mégsem onnan jön a hiba, a közös minta fut — az axios angol hibaszövege sosem.
   */
  const exportErrorMessage = (err: any): string =>
    typeof err?.userMessage === 'string' && err.userMessage
      ? err.userMessage
      : apiErrorMessage(err, 'Hiba történt az exportálás során');

  const exportReport = async () => {
    if (reportType === 'table' && tableOccupancyData) {
      try {
        await reportService.exportTableOccupancyReport(
          selectedDate.format('YYYY-MM-DD'),
          selectedVenueId
        );
      } catch (err: any) {
        console.error('Export error:', err);
        setError(exportErrorMessage(err));
      }
      return;
    }

    if (reportType === 'venue' && venueOccupancyData) {
      try {
        await reportService.exportVenueOccupancyReport(
          startDate.format('YYYY-MM-DD'),
          endDate.format('YYYY-MM-DD')
        );
      } catch (err: any) {
        console.error('Export error:', err);
        setError(exportErrorMessage(err));
      }
      return;
    }

    if (reportMode === 'single' && !reportData.length) return;
    if (reportMode === 'multi' && !multiUserReportData.length) return;

    try {
      const year = selectedMonth.year();
      const month = selectedMonth.month() + 1;

      if (reportMode === 'single') {
        if (reportType === 'player') {
          await reportService.exportPlayerReport(selectedUser, year, month);
        } else {
          await reportService.exportCoachReport(selectedUser, year, month);
        }
      } else {
        if (reportType === 'player') {
          await reportService.exportAllPlayersReport(year, month);
        } else {
          await reportService.exportAllCoachesReport(year, month);
        }
      }
    } catch (err: any) {
      console.error('Export error:', err);
      setError(exportErrorMessage(err));
    }
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'Nincs adat';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Érvénytelen dátum';
      return date.toLocaleDateString('hu-HU');
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Érvénytelen dátum';
    }
  };


  const getUsersByRole = (role: 'player' | 'coach') => {
    const targetRole = role === 'player' ? 'Player' : 'Coach';
    return users.filter(user => user.userRole === targetRole);
  };

  const isAdmin = currentUser?.userRole === 'Administrator';

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Nincs jogosultságod a kimutatások megtekintéséhez.
        </Alert>
      </Box>
    );
  }

  return (
    // [Web UX 9 / T2, 2026-10-13] `adapterLocale="hu"` PÓTOLVA: nélküle az AdapterDayjs KÉNYSZERÍTETTEN angol
    // (`getCurrentLocaleCode: this.locale || 'en'`) — angol hónapnevek („October 2026"), vasárnapos hét, amerikai MM/DD/YYYY mező.
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="hu" localeText={HU_PICKER_LOCALE_TEXT}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Kimutatások
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Szűrési beállítások
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <FormControl fullWidth>
                <InputLabel>Kimutatás típusa</InputLabel>
                <Select
                  value={reportType}
                  label="Kimutatás típusa"
                  onChange={(e) => {
                    setReportType(e.target.value as 'player' | 'coach' | 'venue' | 'table');
                    setSelectedUser('');
                    setReportData([]);
                    setReportSummary(null);
                    setMultiUserReportData([]);
                    setAggregatedSummary(null);
                    setVenueOccupancyData(null);
                    setTableOccupancyData(null);
                    // A másik típus oszlopai mások (pl. „Hátralék" csak edzőnél) — a rendezés és
                    // a névszűrő nem öröklődhet át [Web UX 3. csomag (C), 2026-10-04].
                    setMultiSort(null);
                    setMultiNameFilter('');
                  }}
                >
                  <MenuItem value="player">Játékos részvétel</MenuItem>
                  <MenuItem value="coach">Edző részvétel</MenuItem>
                  <MenuItem value="venue">Helyszín foglaltság</MenuItem>
                  <MenuItem value="table">Asztal foglaltság</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {reportType === 'coach' && (
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Dátumszűrés</FormLabel>
                  <RadioGroup
                    value={dateFilterMode}
                    onChange={(e) => setDateFilterMode(e.target.value as 'monthly' | 'range')}
                    row
                  >
                    <FormControlLabel value="monthly" control={<Radio />} label="Havi" />
                    <FormControlLabel value="range" control={<Radio />} label="Időszak" />
                  </RadioGroup>
                </FormControl>
              </Box>
            )}

            {reportType !== 'venue' && reportType !== 'table' && (
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Kimutatás módja</FormLabel>
                  <RadioGroup
                    row
                    value={reportMode}
                    onChange={(e) => {
                      setReportMode(e.target.value as 'single' | 'multi');
                      setSelectedUser('');
                      setReportData([]);
                      setReportSummary(null);
                      setMultiUserReportData([]);
                      setAggregatedSummary(null);
                    }}
                  >
                    <FormControlLabel value="single" control={<Radio />} label="Egyéni felhasználó" />
                    <FormControlLabel value="multi" control={<Radio />} label="Minden felhasználó" />
                  </RadioGroup>
                </FormControl>
              </Box>
            )}

            {reportMode === 'single' && reportType !== 'venue' && reportType !== 'table' && (
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <FormControl fullWidth>
                  <InputLabel>
                    {reportType === 'player' ? 'Játékos' : 'Edző'}
                  </InputLabel>
                  <Select
                    value={selectedUser}
                    label={reportType === 'player' ? 'Játékos' : 'Edző'}
                    onChange={(e) => setSelectedUser(e.target.value)}
                  >
                    <MenuItem value="">Válassz...</MenuItem>
                    {getUsersByRole(reportType as 'player' | 'coach').map((user) => (
                      <MenuItem key={user.userId} value={user.userId}>
                        {user.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {reportType === 'table' ? (
              <>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <DatePicker
                    label="Dátum"
                    value={selectedDate}
                    onChange={(newDate) => {
                      // A KÉZZEL GÉPELT, tartományon kívüli év is ide jut — klampelve [S1].
                      const clamped = clampToReportRange(newDate);
                      if (clamped) setSelectedDate(clamped);
                    }}
                    format="YYYY-MM-DD"
                    minDate={REPORT_MIN_DATE}
                    maxDate={REPORT_MAX_DATE}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <FormControl fullWidth>
                    <InputLabel>Helyszín</InputLabel>
                    <Select
                      value={selectedVenueId}
                      label="Helyszín"
                      onChange={(e) => setSelectedVenueId(e.target.value as string)}
                    >
                      {venues.map((venue) => (
                        <MenuItem key={venue.venueId} value={venue.venueId}>
                          {venue.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </>
            ) : reportType === 'venue' || (reportType === 'coach' && dateFilterMode === 'range') ? (
              <>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <DatePicker
                    label="Kezdő dátum"
                    value={startDate}
                    onChange={(date) => {
                      // UGYANAZ A MINTA, MINT A MÁSIK KÉT VÁLASZTÓN [S-1, 2026-09-15].
                      // MI VOLT A BAJ: itt `clampToReportRange(date) ?? REPORT_MIN_DATE` állt,
                      // tehát egy teljes, de ÉRVÉNYTELEN gépelt dátum (pl. „2026-02-31", amire a
                      // `clamp` `null`-t ad) a tartomány SZÉLÉRE, 2000-01-01-re ugratta a szűrőt —
                      // a „Dátum" (`:482`) és a „Hónap" (`:554`) választó ugyanerre helyesen
                      // ÉRINTETLENÜL hagyja a mezőt. Egy elgépelés nem írhatja át a szűrőt egy
                      // olyan dátumra, amit a felhasználó nem is írt be.
                      const clamped = clampToReportRange(date);
                      if (clamped) setStartDate(clamped);
                    }}
                    minDate={REPORT_MIN_DATE}
                    maxDate={REPORT_MAX_DATE}
                    sx={{ width: '100%' }}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <DatePicker
                    label="Befejező dátum"
                    value={endDate}
                    onChange={(date) => {
                      // Érvénytelen dátum → ÉRINTETLEN mező, nem a tartomány széle [S-1] —
                      // az indoklás a „Kezdő dátum" választónál.
                      const clamped = clampToReportRange(date);
                      if (clamped) setEndDate(clamped);
                    }}
                    minDate={REPORT_MIN_DATE}
                    maxDate={REPORT_MAX_DATE}
                    sx={{ width: '100%' }}
                  />
                </Box>
              </>
            ) : (
              /*
                A VÁLASZTÓ KORLÁTJA EGYEZIK A SZERVER KORLÁTJÁVAL [javítva 2026-09-15].
                MI VOLT A BAJ: itt nem volt `minDate`/`maxDate`, a szerver viszont
                `[Range(2000, 2100)]`-at érvényesít (`ReportsController.cs:114`,
                `YearOutOfRangeMessage`). Az év-választóban 1999-et kiválasztva a kérés
                400-cal jött vissza — a felület olyan bevitelt engedett, amit a szerver
                garantáltan elutasít. Mostantól a választó le sem engedi kiválasztani.
              */
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <DatePicker
                  label="Hónap"
                  value={selectedMonth}
                  onChange={(date) => {
                    // A KÉZZEL GÉPELT 1999 is ide jut (a rács korlátja csak a KATTINTÁST
                    // szűri) — klampelve, hogy a szerver `[Range(2000,2100)]`-jével egyezzen [S1].
                    const clamped = clampToReportRange(date);
                    if (clamped) {
                      setSelectedMonth(clamped.startOf('month'));
                    }
                  }}
                  views={['year', 'month']}
                  // A SZERVER `[Range(2000, 2100)]`-jével egyező korlát — enélkül az évlista MUI-alapból
                  // 1900-tól indult, és egy 1999-re görgetett „Hónap" 400-at adott ott, ahol korábban üres
                  // riport jött. Ugyanaz a két konstans, mint a másik három választón. [review, 2026-09-15]
                  minDate={REPORT_MIN_DATE}
                  maxDate={REPORT_MAX_DATE}
                  sx={{ width: '100%' }}
                />
              </Box>
            )}

            {(reportType === 'player' || reportType === 'coach') && (
              <Box sx={{ minWidth: 140 }}>
                {/* [Web UX 3. csomag (C), 2026-10-04] — lásd `selectPreviousMonth` (be is tölt, M7). */}
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={selectPreviousMonth}
                  disabled={loading}
                  data-testid="reports-previous-month"
                >
                  Előző hónap
                </Button>
              </Box>
            )}

            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={loadReport}
                disabled={(reportType === 'table' && (!selectedDate || !selectedVenueId)) || ((reportType === 'venue' || (reportType === 'coach' && dateFilterMode === 'range')) && (!startDate || !endDate)) || (reportType !== 'venue' && reportType !== 'table' && !(reportType === 'coach' && dateFilterMode === 'range') && ((reportMode === 'single' && !selectedUser) || !selectedMonth)) || loading}
                startIcon={<ReportIcon />}
              >
                Kimutatás betöltése
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Single User Report Summary */}
        {reportSummary && reportMode === 'single' && (
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
            <Card sx={{ flex: 1, minWidth: 200 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <EventIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4" color="primary">
                  {reportSummary.totalEvents}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Összes esemény
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1, minWidth: 200 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <PersonIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                <Typography variant="h4" color="success.main">
                  {reportSummary.attendedEvents}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Részt vett
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1, minWidth: 200 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <CalendarIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                <Typography variant="h4" color="warning.main">
                  {reportSummary.totalEvents > 0 ? Math.round((reportSummary.attendedEvents / reportSummary.totalEvents) * 100) : 0}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Részvételi arány
                  {/* (i) a MAI szerver-szabály szerint, játékosnál és edzőnél MÁS szöveggel — lásd `RATE_EXPLANATIONS`. */}
                  <InfoHint
                    text={reportType === 'coach' ? RATE_EXPLANATIONS.coachParticipation : RATE_EXPLANATIONS.playerParticipation}
                    testId="info-participation-rate-card"
                  />
                </Typography>
              </CardContent>
            </Card>

            {reportType === 'coach' && reportSummary.totalHours !== undefined && (
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <ScheduleIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                  <Typography variant="h4" color="info.main">
                    {reportSummary.totalHours}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Összesen órák
                  </Typography>
                </CardContent>
              </Card>
            )}

            {reportType === 'coach' && reportSummary.verifiedHours !== undefined && (
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <ScheduleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                  <Typography variant="h4" color="success.main">
                    {reportSummary.verifiedHours}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Igazolt órák
                  </Typography>
                </CardContent>
              </Card>
            )}

            {reportType === 'coach' && reportSummary.totalCoachPayment !== undefined && (
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="warning.main" sx={{ fontSize: 40, mb: 1 }}>
                    💰
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {formatForint(reportSummary.totalCoachPayment ?? 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Edzésekből befolyó összeg
                  </Typography>
                </CardContent>
              </Card>
            )}

            {reportType === 'coach' && reportSummary.hourlyRate !== undefined && (
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary.main" sx={{ fontSize: 40, mb: 1 }}>
                    ⏱️
                  </Typography>
                  <Typography variant="h4" color="primary.main">
                    {formatForint(reportSummary.hourlyRate)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Óradíj
                  </Typography>
                </CardContent>
              </Card>
            )}

            {reportType === 'coach' && reportSummary.totalEarnings !== undefined && (
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="info.main" sx={{ fontSize: 40, mb: 1 }}>
                    💵
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {formatForint(reportSummary.totalEarnings ?? 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Keresett
                  </Typography>
                </CardContent>
              </Card>
            )}

            {reportType === 'coach' && reportSummary.totalCollected !== undefined && (
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="warning.main" sx={{ fontSize: 40, mb: 1 }}>
                    💳
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {formatForint(reportSummary.totalCollected ?? 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Beszedett
                  </Typography>
                </CardContent>
              </Card>
            )}

            {reportType === 'coach' && reportSummary.amountDue !== undefined && (
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontSize: 40, mb: 1, color: reportSummary.amountDue && reportSummary.amountDue > 0 ? 'success.main' : reportSummary.amountDue && reportSummary.amountDue < 0 ? 'error.main' : 'text.secondary' }}>
                    {reportSummary.amountDue && reportSummary.amountDue > 0 ? '✅' : reportSummary.amountDue && reportSummary.amountDue < 0 ? '⚠️' : '➖'}
                  </Typography>
                  <Typography variant="h4" sx={{ color: reportSummary.amountDue && reportSummary.amountDue > 0 ? 'success.main' : reportSummary.amountDue && reportSummary.amountDue < 0 ? 'error.main' : 'text.secondary' }}>
                    {formatForint(reportSummary.amountDue)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Fizetendő (bruttó)
                  </Typography>
                </CardContent>
              </Card>
            )}

            {reportType === 'coach' && reportSummary.trainingPaidOut !== undefined && (
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontSize: 40, mb: 1 }}>
                    🏦
                  </Typography>
                  <Typography variant="h4" color="secondary.main">
                    {formatForint(reportSummary.trainingPaidOut ?? 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Kifizetett (edzés)
                  </Typography>
                </CardContent>
              </Card>
            )}

            {reportType === 'coach' && reportSummary.balance !== undefined && (
              <Card sx={{ flex: 1, minWidth: 200, border: 2, borderColor: reportSummary.balance && reportSummary.balance > 0 ? 'error.main' : 'success.main' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontSize: 40, mb: 1 }}>
                    {reportSummary.balance && reportSummary.balance > 0 ? '💸' : '✅'}
                  </Typography>
                  <Typography variant="h4" sx={{ color: reportSummary.balance && reportSummary.balance > 0 ? 'error.main' : 'success.main' }}>
                    {formatForint(reportSummary.balance)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Egyenleg (hátralék)
                  </Typography>
                </CardContent>
              </Card>
            )}

            {reportType === 'coach' && (reportSummary.extraPaidOut ?? 0) > 0 && (
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontSize: 40, mb: 1 }}>
                    🚗
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {formatForint(reportSummary.extraPaidOut)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Egyéb kifizetések (útiköltség, bónusz)
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/*
              UTÓLAG TÖRÖLT ESEMÉNYEN BESZEDETT / KIFIZETETT PÉNZ — EDZŐI nézet [2026-09-15]
              MI VOLT A BAJ: a 2026-09-14-i javítás helyesen kivette a törölt eseményt a
              RÉSZVÉTELBŐL és az ÓRÁKBÓL, de ezzel a rajta BESZEDETT KÉSZPÉNZ és a rá MÁR
              KIFIZETETT összeg is eltűnt a kimutatásból. MÉRT példa: 2 óra (8 000 Ft óradíj-alap)
              + 15 000 Ft beszedés egy később törölt eseményen → `totalCollected` −15 000,
              `totalEarnings` −8 000, tehát a „Fizetendő" 7 000 Ft-tal MEGNŐTT: a klub követelése
              nyom nélkül eltűnt a kifizetési sorból, miközben a JÁTÉKOS-riport ugyanazt a
              15 000-et „utólag törölt eseményen beszedve" mutatta. A két riport ellentmondott.
              ⚠️ A „Fizetendő"/„Egyenleg" SZÁNDÉKOSAN nem tartalmazza ezt (a tényleges
              kifizetés-számítással kell egyeznie) — ezért a kártya és a lenti sáv KI IS MONDJA.
            */}
            {reportSummary && reportType === 'coach' && ((reportSummary.deletedEventsCollected ?? 0) > 0 || (reportSummary.deletedEventsPaidOut ?? 0) > 0) && (
              <Card
                sx={{ flex: 1, minWidth: 200, borderColor: 'warning.main', borderWidth: 1, borderStyle: 'solid' }}
                data-testid="coach-deleted-events-card"
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontSize: 40, mb: 1 }}>
                    🗑️
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {formatForint(reportSummary.deletedEventsCollected ?? 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Utólag törölt eseményen beszedve — a Fizetendőn KÍVÜL
                  </Typography>
                  {(reportSummary.deletedEventsPaidOut ?? 0) > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Erre már kifizetve: <strong>{formatForint(reportSummary.deletedEventsPaidOut)}</strong> — az Egyenlegen KÍVÜL
                    </Typography>
                  )}
                </CardContent>
              </Card>
            )}

            {reportType === 'player' && reportSummary.totalPaymentAmount !== undefined && (
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main" sx={{ fontSize: 40, mb: 1 }}>
                    💰
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {formatForint(reportSummary.totalPaymentAmount ?? 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Helyszínen fizetett összeg
                  </Typography>
                </CardContent>
              </Card>
            )}

            {reportType === 'player' && reportSummary.paymentRate !== undefined && (
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="info.main" sx={{ fontSize: 40, mb: 1 }}>
                    💳
                  </Typography>
                  {/*
                    NULLA JELENLÉTNÉL „-", NEM „0%" [B-M9, 2026-09-16].

                    MI VOLT A BAJ: ez a kártya 0 jelenlét esetén is „0%"-ot írt ki, ami azt
                    sugallta, hogy a tag NEM FIZETETT — holott nem is volt miért (nem volt egyetlen
                    jelenléte sem az időszakban). A nulla és a „nincs adat" két különböző dolog, és
                    itt PÉNZRŐL szól a szám: az admin ebből a 0%-ból vonhat le következtetést.
                    A táblázat SORA ezt már helyesen kezelte (`:1842`, B-W3) — a kártya és a sor
                    fogalomhasználata csúszott szét, ugyanarra a mennyiségre két állítással.

                    A NEVEZŐ AZ `attendedEvents`, nem a `totalEvents`: a szerver a fizetési arányt a
                    JELENLÉTEK közül számolja (`ReportsController.cs:643`,
                    `PaymentRate = attendedEvents > 0 ? … / attendedEvents : 0`), és a lenti felirat
                    is ezt mondja. Ugyanaz a nevező, mint a táblázat sorában.
                  */}
                  <Typography variant="h4" color="info.main">
                    {reportSummary.attendedEvents > 0
                      ? `${Math.round(reportSummary.paymentRate || 0)}%`
                      : NO_AMOUNT_TEXT}
                  </Typography>
                  {/*
                    A FELIRAT MEGMONDJA A BÁZIST [2026-09-14, harmadik kör]. MI VOLT A BAJ: a
                    szám bázisa egyszer már NÉMÁN megváltozott (`attendedEvents` → `totalEvents`),
                    és mérve 75% helyett 37,5% jelent meg ugyanezzel a felirattal — a chip-szín
                    miatt egy hiánytalanul fizető tag sora VÖRÖS lett. A bázis visszaállt a
                    jelenlétekre, és mostantól a felirat is kimondja.
                  */}
                  <Typography variant="body2" color="text.secondary">
                    Fizetési arány (a jelenlétei közül)
                    <InfoHint text={RATE_EXPLANATIONS.playerPayment} testId="info-payment-rate-card" />
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/*
              UTÓLAG TÖRÖLT ESEMÉNYEN BESZEDETT PÉNZ — külön kártya [NB-2, 2026-09-14]
              MI VOLT A BAJ: a backend 2026-09-14 óta KÜLÖN mezőben adja ezt
              (`ReportsController.cs` → `DeletedEventsPaidCount`/`DeletedEventsPaymentAmount`),
              de a felület NEM olvasta ki — így az admin három ellentmondó számot látott
              magyarázat nélkül. MÉRVE egy játékosra, 2026/09: `attendedEvents: 1`,
              `paymentRate: 0`, mégis `totalPaymentAmount: 1 000 000 Ft`, és a teljes összeg egy
              UTÓLAG TÖRÖLT eseményről jött. Mostantól ez a kártya + a lenti magyarázó sáv mondja
              ki, honnan jön a pénz.
              ⚠️ „utólag törölt" = az ADMIN törölte az eseményt. NEM a „lemondott edzés", ami a
              JÁTÉKOS leiratkozása és pótlási kreditet ad (`TrainingBalance.tsx:629`).
            */}
            {/*
              ⚠️ A FELTÉTEL SZÁNDÉKOSAN A DARABSZÁM, NEM AZ ÖSSZEG [NB-3, javítva 2026-09-14].
              KORÁBBAN ez a kártya az ÖSSZEGRE (`deletedEventsPaymentAmount > 0`), a lenti
              magyarázó sáv viszont a DARABSZÁMRA (`deletedEventsPaidCount > 0`) kapcsolt.
              MÉRVE (lokális DB): 212 olyan jelenléti sor van, ahol `IsActive=0 AND
              HasPaidOnSite=1 AND (PaymentAmount IS NULL OR =0)` — ilyen sornál a sáv megjelent
              („a rajtuk beszedett 0 Ft viszont benne van…"), a kártya viszont NEM, tehát a
              magyarázat egy nem létező kártyára hivatkozott. Játékos-hónap szintjén ez ma nem
              állt elő (lappangó hiba), de a két feltételnek AZONOSNAK kell lennie: mostantól
              mindkettőt a `deletedEventsPaidCount` kapcsolja (a sáv PÉNZ-mondatát is), a sáv
              LÉTEZÉSÉT pedig a `deletedEventsTotalCount` — az a fizetés nélküli törölteket is
              magában foglalja (lásd NB-6).
            */}
            {reportType === 'player' && (reportSummary.deletedEventsPaidCount ?? 0) > 0 && (
              <Card sx={{ flex: 1, minWidth: 200, borderColor: 'warning.main', borderWidth: 1, borderStyle: 'solid' }} data-testid="deleted-events-card">
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontSize: 40, mb: 1 }}>
                    🗑️
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {formatForint(reportSummary.deletedEventsPaymentAmount ?? 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ebből utólag törölt eseményen beszedve ({reportSummary.deletedEventsPaidCount ?? 0} esemény)
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/*
              „ESEMÉNYEK TÍPUS SZERINT" — csak AKKOR, ha van adat [NB-4, javítva 2026-09-14].
              MI VOLT A BAJ: a szerver EZT A KULCSOT SOHA NEM KÜLDI (mérve az élő API-n: sem a
              `player-participation`, sem a `coach-participation` `summary`-jában nincs
              `eventTypeBreakdown`), ezért a kártya MINDIG üresen renderelt — egy címke egyetlen
              chip nélkül. Egy örökre üres kártya azt a látszatot adja, hogy „nincs ilyen
              esemény", pedig valójában az ADAT hiányzik. Amíg a végpont nem adja meg, a kártya
              ne jelenjen meg; ha egyszer megadja, magától visszatér.
            */}
            {(reportSummary.eventTypeBreakdown?.length ?? 0) > 0 && (
              <Card sx={{ flex: 1, minWidth: 200 }} data-testid="event-type-breakdown-card">
                <CardContent sx={{ textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                    {(reportSummary.eventTypeBreakdown || []).map((type, index) => (
                      <Chip
                        key={index}
                        label={`${type.eventType}: ${type.count}`}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Események típus szerint
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/*
              MAGYARÁZÓ SÁV — enélkül a fenti számok néma eltérést adnak [NB-2, 2026-09-14].
              Teljes szélességű flex-elem (a szülő `flexWrap: 'wrap'`), tehát saját sorba kerül.
              SZÁNDÉKOSAN „utólag törölt", SOHA nem „lemondott": a „lemondott edzés" a felületen
              már MÁST jelent (`TrainingBalance.tsx:629` — a játékos leiratkozása, pótlási kredit).
            */}
            {reportType === 'player' && (reportSummary.deletedEventsTotalCount ?? reportSummary.deletedEventsPaidCount ?? 0) > 0 && (
              <Box sx={{ width: '100%' }}>
                <Alert severity="warning" data-testid="deleted-events-explanation">
                  A hónapban <strong>{reportSummary.deletedEventsTotalCount ?? reportSummary.deletedEventsPaidCount ?? 0} olyan
                  esemény</strong> is volt, amelyet <strong>utólag töröltek</strong>
                  {(reportSummary.deletedEventsAttendedCount ?? 0) > 0 && (
                    <> — ebből <strong>{reportSummary.deletedEventsAttendedCount} esetben „jelen volt"</strong> jelölés
                    is rögzült</>
                  )}
                  . Ezek <strong>egyáltalán nem számítanak bele</strong> a részvételi számokba
                  (Összes esemény / Részt vett / Részvételi arány / Fizetési arány), mert az esemény
                  végül nem történt meg. A <strong>„Helyszínen fizetett összeg"</strong> viszont
                  IGEN tartalmazza őket — és az exportált fájlban a <strong>„Fizetett (db)"</strong>{' '}
                  darabszám is —, mert az a BEFOLYT PÉNZRŐL szól. Ezért lehet a fájlban a
                  „Fizetett (db)" nagyobb az „Összes esemény"-nél.
                  {/*
                    [NB-a, 2026-09-14] MI VOLT A BAJ: a felsorolás azt sugallta, hogy a törölt
                    sorok SEMMIBE nem számítanak bele, holott a fizetett-darabszám és a beszedett
                    összeg tartalmazza őket. Mérve a CSV-ben: `Összes esemény;3` és `Fizetve;767`
                    egymás alatt, és a 767-et semmi nem magyarázta.
                    ⚠️ A SZÓHASZNÁLAT a FELÜLETÉT követi: a képernyőn nincs „Fizetve (db)" elem
                    (a kártya neve „Helyszínen fizetett összeg", a darabszám csak az exportban
                    van), a többjátékos táblázat oszlopa pedig „Fizetett" — ezért „Fizetett (db)"
                    a fájlban is, nem „Fizetve".
                  */}
                  {(reportSummary.deletedEventsPaidCount ?? 0) > 0 && (
                    <>
                      {' '}Közülük <strong>{reportSummary.deletedEventsPaidCount} eseményen</strong> viszont
                      volt helyszíni beszedés, és az ott beszedett{' '}
                      <strong>{formatForint(reportSummary.deletedEventsPaymentAmount ?? 0)}</strong> BENNE VAN a
                      „Helyszínen fizetett összeg"-ben, mert az a pénz tényleg befolyt. Ezért lehet, hogy a
                      beszedett összeg nem egyezik a részvételi számokkal. Ezek az események a lenti
                      táblázatban „utólag törölve" jelöléssel szerepelnek; a beszedés nélküli töröltek a
                      táblázatban nem jelennek meg.
                    </>
                  )}
                </Alert>
              </Box>
            )}

            {/*
              MAGYARÁZÓ SÁV AZ EDZŐI NÉZETHEZ [2026-09-15] — enélkül a „Fizetendő"/„Egyenleg" és a
              törölt-eseményi pénz két, egymásnak látszólag ellentmondó szám lenne magyarázat
              nélkül. A sáv KIMONDJA a döntést: a részvétel/óra szűr, a pénz látszik, de a
              kifizetési sorba SZÁNDÉKOSAN nincs beolvasztva (a tényleges kifizetés-számítással
              kell egyeznie — `CoachPaymentController.cs:101`).
              ⚠️ SZÓHASZNÁLAT: „utólag törölt" = az ADMIN törölte az eseményt. SOHA nem
              „lemondott" — az a játékos leiratkozása (pótlási kredit, `TrainingBalance.tsx:629`).
            */}
            {reportSummary && reportType === 'coach' && (reportSummary.deletedEventsTotalCount ?? 0) > 0 && (
              <Box sx={{ width: '100%' }}>
                <Alert severity="warning" data-testid="coach-deleted-events-explanation">
                  A hónapban <strong>{reportSummary.deletedEventsTotalCount} olyan edzés</strong> is
                  volt, amelyet <strong>utólag töröltek</strong>
                  {(reportSummary.deletedEventsAttendedCount ?? 0) > 0 && (
                    <> — ebből <strong>{reportSummary.deletedEventsAttendedCount} esetben „jelen volt"</strong> jelölés
                    is rögzült</>
                  )}
                  . Ezek <strong>nem számítanak bele</strong> az „Összes óra", „Igazolt óra",
                  „Keresett", „Beszedett", „Fizetendő" és „Egyenleg" értékekbe, mert az edzés végül
                  nem történt meg — és a tényleges kifizetés-számítás is így szűr.
                  {(reportSummary.deletedEventsHours ?? 0) > 0 && (
                    <> Az így kimaradó óraszám: <strong>{reportSummary.deletedEventsHours} óra</strong>.</>
                  )}
                  {(reportSummary.deletedEventsCollected ?? 0) > 0 && (
                    <>
                      {' '}A törölt edzéseken viszont <strong>{formatForint(reportSummary.deletedEventsCollected)}</strong>{' '}
                      készpénzt beszedtek, ami TÉNYLEGESEN befolyt — ez az összeg a
                      <strong> „Fizetendő"-n KÍVÜL</strong> van, tehát a klubbal külön kell elszámolni.
                    </>
                  )}
                  {(reportSummary.deletedEventsPaidOut ?? 0) > 0 && (
                    <>
                      {' '}Ezekre az edzésekre korábban már{' '}
                      <strong>{formatForint(reportSummary.deletedEventsPaidOut)}</strong> kifizetés is történt, ami
                      az <strong>„Egyenleg"-en KÍVÜL</strong> van.
                    </>
                  )}
                </Alert>
              </Box>
            )}
          </Box>
        )}

        {/* Multi-User Aggregated Summary */}
        {aggregatedSummary && reportMode === 'multi' && (
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
            <Card sx={{ flex: 1, minWidth: 200 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <PersonIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4" color="primary">
                  {aggregatedSummary.totalUsers}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Összes {reportType === 'player' ? 'játékos' : 'edző'}
                </Typography>
              </CardContent>
            </Card>

            {/*
              „Klub összes eseménye" — SZÁNDÉKOSAN NEM „Összes esemény" [NB-e, 2026-09-14].
              MI VOLT A BAJ: ugyanez a magyar felirat szerepelt a TÁBLÁZAT oszlopán is, ott
              viszont az ADOTT tag jelenléti sorainak számát jelenti. Egy képernyőn két fogalom
              egy néven — ezért lehetett a CSV-ben `Összes esemény;6` közvetlenül az
              `Átlagos események/felhasználó;0` alatt, magyarázat nélkül.
            */}
            <Card sx={{ flex: 1, minWidth: 200 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <EventIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                <Typography variant="h4" color="success.main">
                  {aggregatedSummary.totalEvents}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Klub összes eseménye
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1, minWidth: 200 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <CalendarIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                <Typography variant="h4" color="warning.main">
                  {(aggregatedSummary.averageParticipationRate && aggregatedSummary.averageParticipationRate > 0) ? Math.round(aggregatedSummary.averageParticipationRate) : 0}%
                </Typography>
                {/*
                  A NEVEZŐ KIMONDVA [NB-d, 2026-09-14]. MI VOLT A BAJ: az átlag MINDEN aktív
                  tagra vonatkozik, annak is, akinek egyetlen jelenléti sora sem volt. MÉRVE
                  2026/03: klub-átlag 0,9%, miközben a 4 TÉNYLEGES résztvevő átlaga 37,5% —
                  175 tagból 170-nek nulla sora van. A számot nem cseréltük ki (ez a
                  „klub-fegyelem" mutatója), de a felirat megmondja, mire átlagol.
                */}
                <Typography variant="body2" color="text.secondary">
                  Átlagos részvételi arány
                  {aggregatedSummary.totalUsers ? ` (mind a ${aggregatedSummary.totalUsers} tagra)` : ''}
                </Typography>
              </CardContent>
            </Card>

            {reportType === 'coach' && aggregatedSummary.totalHours !== undefined && (
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <ScheduleIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                  <Typography variant="h4" color="info.main">
                    {aggregatedSummary.totalHours}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Összesen órák
                  </Typography>
                </CardContent>
              </Card>
            )}

            {reportType === 'coach' && aggregatedSummary.averageHoursPerCoach !== undefined && (
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <ScheduleIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                  <Typography variant="h4" color="secondary.main">
                    {aggregatedSummary.averageHoursPerCoach}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Átlag óra/edző
                  </Typography>
                </CardContent>
              </Card>
            )}

            {reportType === 'coach' && aggregatedSummary.totalVerifiedHours !== undefined && (
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <ScheduleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                  <Typography variant="h4" color="success.main">
                    {aggregatedSummary.totalVerifiedHours}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Összes igazolt órák
                  </Typography>
                </CardContent>
              </Card>
            )}

            {reportType === 'coach' && aggregatedSummary.averageVerifiedHoursPerCoach !== undefined && (
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <ScheduleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                  <Typography variant="h4" color="success.main">
                    {aggregatedSummary.averageVerifiedHoursPerCoach}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Átlag igazolt óra/edző
                  </Typography>
                </CardContent>
              </Card>
            )}

            {reportType === 'player' && aggregatedSummary.averagePaymentRate !== undefined && (
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="info.main" sx={{ fontSize: 40, mb: 1 }}>
                    💰
                  </Typography>
                  {/*
                    NULLA TAGNÁL „-", NEM „0%" [B-M9, 2026-09-16].

                    ITT KORÁBBAN `(… && … > 0) ? Math.round(…) : 0` állt, ami két hibát is vitt:
                    a „nincs adat" esetet 0%-nak írta ki, a `> 0` kapu pedig érdemben semmit nem
                    szűrt (0-ra is 0-t adott). Az ÁTLAG NEVEZŐJE minden aktív tag
                    (`ReportsController.cs:1404` → `userReports.Average(...)`, és
                    `userReports = players.Select(...)` egy-az-egyben, `:1316`), tehát a felületen a
                    nevező a `totalUsers` (`:1399` `TotalUsers = players.Count`) — ha AZ nulla,
                    nincs miből átlagot képezni, és „0%"-ot kiírni félrevezető.
                    ⚠️ Ha vannak tagok, de az átlag valóban 0, a „0%" HELYES és meg is jelenik —
                    a nulla nem tűnhet el csak azért, mert kellemetlen.
                  */}
                  <Typography variant="h4" color="info.main">
                    {(aggregatedSummary.totalUsers ?? 0) > 0
                      ? `${Math.round(aggregatedSummary.averagePaymentRate ?? 0)}%`
                      : NO_AMOUNT_TEXT}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Átlagos fizetési arány
                    {aggregatedSummary.totalUsers ? ` (mind a ${aggregatedSummary.totalUsers} tagra)` : ''}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {reportType === 'player' && aggregatedSummary.totalPaymentAmount !== undefined && (
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main" sx={{ fontSize: 40, mb: 1 }}>
                    💵
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {formatForint(aggregatedSummary.totalPaymentAmount ?? 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Összes befizetett összeg
                  </Typography>
                </CardContent>
              </Card>
            )}

            {reportType === 'coach' && aggregatedSummary.totalEarnings !== undefined && (
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="info.main" sx={{ fontSize: 40, mb: 1 }}>
                    💰
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {formatForint(aggregatedSummary.totalEarnings ?? 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Összes keresett összeg
                  </Typography>
                </CardContent>
              </Card>
            )}

            {reportType === 'coach' && aggregatedSummary.totalCollected !== undefined && (
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="warning.main" sx={{ fontSize: 40, mb: 1 }}>
                    💵
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {formatForint(aggregatedSummary.totalCollected ?? 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Összes beszedett összeg
                  </Typography>
                </CardContent>
              </Card>
            )}

            {reportType === 'coach' && aggregatedSummary.totalAmountDue !== undefined && (
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main" sx={{ fontSize: 40, mb: 1 }}>
                    📋
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {formatForint(aggregatedSummary.totalAmountDue ?? 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Összes fizetendő összeg
                  </Typography>
                </CardContent>
              </Card>
            )}

            {reportType === 'coach' && aggregatedSummary.totalTrainingPaidOut !== undefined && (
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontSize: 40, mb: 1 }}>
                    🏦
                  </Typography>
                  <Typography variant="h4" color="secondary.main">
                    {formatForint(aggregatedSummary.totalTrainingPaidOut ?? 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Összes kifizetett (edzés)
                  </Typography>
                </CardContent>
              </Card>
            )}

            {reportType === 'coach' && (aggregatedSummary.totalExtraPaidOut ?? 0) > 0 && (
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontSize: 40, mb: 1 }}>
                    🚗
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {formatForint(aggregatedSummary.totalExtraPaidOut)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Összes egyéb kifizetés
                  </Typography>
                </CardContent>
              </Card>
            )}

            {reportType === 'coach' && aggregatedSummary.totalBalance !== undefined && (
              <Card sx={{ flex: 1, minWidth: 200, border: 2, borderColor: aggregatedSummary.totalBalance && aggregatedSummary.totalBalance > 0 ? 'error.main' : 'success.main' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontSize: 40, mb: 1 }}>
                    {aggregatedSummary.totalBalance && aggregatedSummary.totalBalance > 0 ? '💸' : '✅'}
                  </Typography>
                  <Typography variant="h4" sx={{ color: aggregatedSummary.totalBalance && aggregatedSummary.totalBalance > 0 ? 'error.main' : 'success.main' }}>
                    {formatForint(aggregatedSummary.totalBalance ?? 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Összes hátralék
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/*
              AZ ÖSSZES EDZŐ TÖRÖLT-ESEMÉNYI PÉNZE [2026-09-15]. ⚠️ A backend az összesítést a
              hónap MINDEN törölt edzői eseményéből számolja, NEM a táblázat sorainak összegéből:
              egy olyan edző, akinek a hónapban CSAK törölt edzése volt, nem kap sort (a lista az
              aktív eseményekből áll össze), a nála lévő készpénz viszont a klub pénze — ezért az
              összegzésben MINDIG teljes. Ezt a felirat is kimondja.
            */}
            {reportType === 'coach' && ((aggregatedSummary.deletedEventsCollected ?? 0) > 0 || (aggregatedSummary.deletedEventsPaidOut ?? 0) > 0) && (
              <Card
                sx={{ flex: 1, minWidth: 200, borderColor: 'warning.main', borderWidth: 1, borderStyle: 'solid' }}
                data-testid="coach-deleted-events-aggregated-card"
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontSize: 40, mb: 1 }}>
                    🗑️
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {formatForint(aggregatedSummary.deletedEventsCollected ?? 0)}
                  </Typography>
                  {/*
                    A FELIRAT MINDKÉT IRÁNYT KIMONDJA [K-2, 2026-09-15].
                    MI VOLT A BAJ: a felirat csak azt írta, hogy „a sorok összegénél több lehet".
                    A PÉNZRE ez igaz (sorhiány + `RecordedBy`-szűrés, lásd
                    `ReportsController.cs:1700-1731`), a DARABSZÁMRA viszont FORDÍTVA: a kártya
                    KÜLÖNBÖZŐ eseményeket számol (`…Select(de => de.EventId).Distinct().Count()`),
                    a táblázat sorai pedig hozzárendelésenként 1-et (`coachDeletedEvents.Count`),
                    tehát két edző EGY törölt edzésén a sorokban 1 db + 1 db áll, a kártyán mégis
                    „(1 edzés)". Az admin a régi felirat alapján a kártyát hitte hiányosnak.
                  */}
                  <Typography variant="body2" color="text.secondary">
                    Utólag törölt edzéseken beszedve ({aggregatedSummary.deletedEventsTotalCount ?? 0} különböző
                    edzés; egy edzés több edző soránál is szerepelhet) — a „Fizetendő"-n KÍVÜL, és
                    az összeg a sorok összegénél több lehet
                  </Typography>
                  {(aggregatedSummary.deletedEventsPaidOut ?? 0) > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Erre már kifizetve: <strong>{formatForint(aggregatedSummary.deletedEventsPaidOut)}</strong>
                    </Typography>
                  )}
                </CardContent>
              </Card>
            )}

            {/*
              UTÓLAG TÖRÖLT ESEMÉNYEK A TÖBBJÁTÉKOS ÖSSZEGZÉSBEN [B-1, 2026-09-14].
              MI VOLT A BAJ: a többjátékos kimutatás nem szűrt `IsActive`-ra, ÉS a többjátékos
              sorhoz/összegzéshez semmilyen bontás és magyarázó sáv nem tartozott (a fenti két
              blokk csak az egy-játékos összegzésre fut). Mivel a két nézet UGYANARRÓL a
              képernyőről, egy legördülőből érhető el, az admin ugyanarra a játékosra 0%-ot
              látott az egyikben és 65,2%-ot a másikban — magyarázat nélkül.
              A feltétel SZÁNDÉKOSAN ugyanaz a párosítás, mint az egy-játékos nézetben
              (kártya ↔ `deletedEventsPaidCount`, sáv ↔ `deletedEventsTotalCount`) [NB-3].
            */}
            {reportType === 'player' && (aggregatedSummary.deletedEventsPaidCount ?? 0) > 0 && (
              <Card sx={{ flex: 1, minWidth: 200, borderColor: 'warning.main', borderWidth: 1, borderStyle: 'solid' }} data-testid="multi-deleted-events-card">
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontSize: 40, mb: 1 }}>
                    🗑️
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {formatForint(aggregatedSummary.deletedEventsPaymentAmount ?? 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ebből utólag törölt eseményen beszedve ({aggregatedSummary.deletedEventsPaidCount ?? 0} esemény)
                  </Typography>
                </CardContent>
              </Card>
            )}

            {reportType === 'player' && (aggregatedSummary.deletedEventsTotalCount ?? 0) > 0 && (
              <Box sx={{ width: '100%' }}>
                <Alert severity="warning" data-testid="multi-deleted-events-explanation">
                  A hónapban <strong>{aggregatedSummary.deletedEventsTotalCount} olyan jelenléti sor</strong> is
                  keletkezett, amelynek az eseményét <strong>utólag törölték</strong>
                  {(aggregatedSummary.deletedEventsAttendedCount ?? 0) > 0 && (
                    <> — ebből <strong>{aggregatedSummary.deletedEventsAttendedCount} esetben „jelen volt"</strong> jelölés
                    is rögzült</>
                  )}
                  . Ezek <strong>egyáltalán nem számítanak bele</strong> a táblázat részvételi számaiba
                  (Összes esemény / Részt vett / Részvételi arány / Fizetési arány), mert az esemény
                  végül nem történt meg. A táblázat <strong>„Fizetett"</strong> és
                  <strong> „Összes fizetett (Ft)"</strong> oszlopa viszont IGEN tartalmazza őket —
                  az a kettő a BEFOLYT PÉNZRŐL szól, ezért lehet a „Fizetett" oszlop száma nagyobb
                  az „Összes esemény" oszlopénál.
                  {/* [NB-a, 2026-09-14] — a szavak a TÁBLÁZAT oszlopneveit követik (`:1411` és
                      lentebb), hogy az admin össze tudja párosítani a mondatot a képernyővel. */}
                  {(aggregatedSummary.deletedEventsPaidCount ?? 0) > 0 && (
                    <>
                      {' '}Közülük <strong>{aggregatedSummary.deletedEventsPaidCount} eseményen</strong> volt
                      helyszíni beszedés, és az ott beszedett{' '}
                      <strong>{formatForint(aggregatedSummary.deletedEventsPaymentAmount ?? 0)}</strong> BENNE VAN az
                      „Összes befizetett összeg"-ben és a táblázat „Összes fizetett" oszlopában, mert az a
                      pénz tényleg befolyt. A játékosonkénti bontás a táblázat „Utólag törölt" oszlopában
                      látszik.
                    </>
                  )}
                </Alert>
              </Box>
            )}

            {/*
              MÁR DEAKTIVÁLT TAGOKTÓL BESZEDETT PÉNZ — külön sáv [NB-c, 2026-09-14].
              MI VOLT A BAJ: a táblázat és az „Összes játékos" szám csak az AKTÍV tagokat mutatja
              (`ReportsController.cs` → `u.IsActive`), az egy-játékos riport viszont a felhasználó
              aktív állapotát egyáltalán nem vizsgálja. Így a deaktivált tagoktól beszedett
              készpénz NYOM NÉLKÜL eltűnt a klub-összegzésből, és a két nézet klubszinten nem
              egyezett. MÉRVE 2026/09: 19 fizetett sor / 74 029 Ft nem látszott a 2 672 100 Ft-ban
              (a tényleges játékosi beszedés 2 746 129 Ft volt).
              A DÖNTÉS: a táblázat marad a JELENLEGI tagságról (ez klub-fogalom), de a pénz nem
              tűnhet el — a backend külön, megnevezett mezőben adja, és ez a sáv kimondja.
            */}
            {reportType === 'player' && (aggregatedSummary.inactiveUsersPaidCount ?? 0) > 0 && (
              <Box sx={{ width: '100%' }}>
                <Alert severity="info" data-testid="inactive-users-explanation">
                  A fenti számok <strong>csak a jelenleg aktív tagokról</strong> szólnak. A hónapban
                  ezen kívül <strong>{aggregatedSummary.inactiveUsersCount ?? 0} már deaktivált
                  tagtól</strong> is történt helyszíni beszedés,{' '}
                  <strong>{aggregatedSummary.inactiveUsersPaidCount ?? 0} alkalommal</strong>,
                  összesen <strong>{formatForint(aggregatedSummary.inactiveUsersPaymentAmount ?? 0)}</strong> —
                  ez az összeg a táblázatban és az „Összes befizetett összeg"-ben NEM szerepel,
                  mert ők már nem tagok. A pénz viszont befolyt, ezért itt látszik.
                </Alert>
              </Box>
            )}

            {/*
              AZ ÁTLAGOK NEVEZŐJE [NB-d, 2026-09-14]: minden aktív tag beleszámít, annak is, akinek
              egyetlen jelenléti sora sem volt. Mérve 2026/03: klub-átlag 0,9%, a 4 tényleges
              résztvevő átlaga 37,5%. A számokat nem cseréltük ki, de a nevező ki van mondva.
            */}
            {reportType === 'player'
              && aggregatedSummary.usersWithAnyEventCount !== undefined
              && (aggregatedSummary.totalUsers ?? 0) > 0
              && aggregatedSummary.usersWithAnyEventCount < (aggregatedSummary.totalUsers ?? 0) && (
              <Box sx={{ width: '100%' }}>
                <Alert severity="info" data-testid="average-denominator-explanation">
                  A fenti <strong>átlagok mind a {aggregatedSummary.totalUsers} aktív tagra</strong>{' '}
                  vonatkoznak, miközben a hónapban csak{' '}
                  <strong>{aggregatedSummary.usersWithAnyEventCount} tagnak</strong> volt egyáltalán
                  jelenléti sora. Ezért mutathat a klub-átlag közel nullát akkor is, ha a tényleges
                  résztvevők aránya ennél jóval magasabb — a tagonkénti értékek a táblázatban
                  látszanak.
                </Alert>
              </Box>
            )}
          </Box>
        )}

        {/* Single User Report Data */}
        {reportData.length > 0 && reportMode === 'single' && (
          <Paper sx={{ mb: 3 }}>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {/*
                A FEJLÉC SZÁMA A MEGJELENÍTETT SOROKAT jelenti, és ezek között UTÓLAG TÖRÖLT
                események is lehetnek [NB-k, javítva 2026-09-14]. MI VOLT A BAJ: a fejléc
                „Részletes kimutatás (770 esemény)"-t írt, miközben az „Összes esemény" kártya
                3-at — mert a részletező a törölt-DE-fizetett sorokat is mutatja, a kártya
                viszont csak az aktívakat. A magyarázó sáv utalt rá, de a FEJLÉC száma
                feloldatlan maradt. Most a fejléc maga mondja meg, mit számolt.
              */}
              <Typography variant="h6">
                {(reportSummary?.deletedEventsPaidCount ?? 0) > 0
                  ? `Részletes kimutatás (${reportData.length} sor — az utólag törölt, de befizetéssel járó eseményekkel együtt)`
                  : `Részletes kimutatás (${reportData.length} esemény)`}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={exportReport}
              >
                {/* A letöltött fájl .csv — a felirat korábban „Excel exportálás" volt [2026-09-14] */}
                CSV exportálás
              </Button>
            </Box>
            <Divider />

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Esemény neve</TableCell>
                    <TableCell>Dátum</TableCell>
                    <TableCell>Időpont</TableCell>
                    {reportType === 'coach' && <TableCell align="center">Időtartam (óra)</TableCell>}
                    <TableCell>Típus</TableCell>
                    <TableCell>Helyszín</TableCell>
                    <TableCell align="center">Részvétel</TableCell>
                    {reportType === 'player' && (
                      <>
                        <TableCell align="center">Helyszínen fizetett</TableCell>
                        <TableCell align="center">Fizetett összeg (Ft)</TableCell>
                      </>
                    )}
                    {reportType === 'coach' && (
                      <>
                        <TableCell align="center">Kapott összeg (Ft)</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={reportType === 'player' ? 8 : (reportType === 'coach' ? 8 : 6)} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : (
                    reportData.map((event) => (
                      <TableRow
                        key={event.eventId}
                        // Az utólag törölt esemény sora halványabb: a pénz benne van az összegben,
                        // de az esemény nem történt meg. [NB-2, 2026-09-14]
                        sx={event.isEventDeleted ? { backgroundColor: 'action.hover' } : undefined}
                      >
                        <TableCell>
                          {event.eventName || 'Névtelen esemény'}
                          {/*
                            JELÖLÉS: a backend `IsEventDeleted`-et küld (`ReportsController.cs`),
                            eddig a felület jelölés NÉLKÜL, valódi részvételként mutatta a törölt
                            eseményt. SZÓHASZNÁLAT: „utólag törölve", NEM „lemondva" — a „lemondott"
                            a játékos leiratkozását jelenti (`TrainingBalance.tsx:629`).
                          */}
                          {event.isEventDeleted && (
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label="utólag törölve"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>{formatDate(event.eventDate)}</TableCell>
                        <TableCell>
                          {event.eventTime || '--:-- - --:--'}
                          {reportType === 'coach' && event.endTime && ` - ${event.endTime}`}
                        </TableCell>
                        {reportType === 'coach' && (
                          <TableCell align="center">
                            {event.duration ? `${event.duration} óra` : 'N/A'}
                            {/* Részleges edzői jelenlét: az igazolt óra kevesebb az esemény hosszánál */}
                            {event.isPresent && event.approvedDuration != null && event.duration != null && event.approvedDuration < event.duration && (
                              <Chip size="small" color="warning" variant="outlined" label={`igazolt: ${event.approvedDuration} óra`} sx={{ ml: 1 }} />
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          <Chip
                            label={event.eventType || 'Ismeretlen típus'}
                            size="small"
                            sx={{ backgroundColor: '#999', color: 'white' }}
                          />
                        </TableCell>
                        <TableCell>{event.venue || 'Nincs helyszín'}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={event.isPresent === true ? 'Igen' : event.isPresent === false ? 'Nem' : 'Nincs adat'}
                            color={event.isPresent === true ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        {reportType === 'player' && (
                          <>
                            <TableCell align="center">
                              <Chip
                                label={event.hasPaidOnSite === true ? 'Igen' : event.hasPaidOnSite === false ? 'Nem' : 'Nincs adat'}
                                color={event.hasPaidOnSite === true ? 'success' : event.hasPaidOnSite === false ? 'warning' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={formatForint(event.paymentAmount)}
                                color={event.paymentAmount ? 'info' : 'default'}
                                size="small"
                              />
                            </TableCell>
                          </>
                        )}
                        {reportType === 'coach' && (
                          <>
                            <TableCell align="center">
                              <Chip
                                label={formatForint(event.paymentAmount)}
                                color={event.paymentAmount ? 'warning' : 'default'}
                                size="small"
                              />
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Multi-User Report Data */}
        {multiUserReportData.length > 0 && reportMode === 'multi' && (
          <Paper sx={{ mb: 3 }}>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {reportType === 'player' ? 'Játékosok' : 'Edzők'} összesített kimutatása ({multiUserReportData.length} fő)
              </Typography>
              {/*
                ⚠️ A CSV-EXPORT A TELJES LISTÁT EXPORTÁLJA, NEM A SZŰRTET ÉS NEM A RENDEZETTET
                [Web UX 3. csomag (C), 2026-10-04]: a fájlt a SZERVER állítja elő ugyanabból a
                lekérdezésből (`exportAllPlayersReport`/`exportAllCoachesReport`, csak év+hónap
                paraméterrel), a névszűrő és a rendezés pedig kizárólag ezen a képernyőn él. Hogy ez ne
                érje meglepetésként az admint, szűrt állapotban a kereső alatti sor ki is mondja.
              */}
              <Button
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={exportReport}
              >
                CSV exportálás
              </Button>
            </Box>
            {/*
              NÉVKERESŐ [Web UX 3. csomag (C), 2026-10-04] — kliens-oldali szűrés a BETÖLTÖTT listán,
              kis- és ékezet-érzéketlenül (a közös `utils/nameSearch.ts` `normalizeForSearch`).
            */}
            <Box sx={{ px: 2, pb: 2 }}>
              <TextField
                size="small"
                label="Név keresése"
                value={multiNameFilter}
                onChange={(e) => setMultiNameFilter(e.target.value)}
                inputProps={{ 'data-testid': 'reports-name-filter' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 260 }}
              />
              {multiNameFilter.trim() !== '' && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }} data-testid="reports-filter-note">
                  Szűrve: {displayedMultiUsers.length} / {multiUserReportData.length} fő — a CSV-export a teljes listát tartalmazza.
                </Typography>
              )}
            </Box>
            <Divider />

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {/*
                      RENDEZHETŐ FEJLÉC + RÖGZÍTETT NÉVOSZLOP [Web UX 3. csomag (C), 2026-10-04].
                      Minden fejléc-szöveg VÁLTOZATLAN (E2E-k és a Jest `cellOf` a pontos szöveget
                      keresik); a `TableSortLabel` csak köré kerül. Az első kattintás a névnél
                      ábécérend, a számoknál a LEGNAGYOBB elöl („kinek tartozunk a legtöbbel?");
                      a „-"/„Nincs adat" mindig a végén (`utils/reportSort.ts`).
                    */}
                    {sortHeader('name', 'Név', { align: 'left', sticky: true })}
                    {sortHeader('totalEvents', 'Összes esemény')}
                    {sortHeader('attendedEvents', 'Részt vett')}
                    {sortHeader('participationRate', 'Részvételi arány', {
                      info: reportType === 'coach' ? RATE_EXPLANATIONS.coachParticipation : RATE_EXPLANATIONS.playerParticipation,
                      infoTestId: 'info-participation-rate-header',
                    })}
                    {reportType === 'coach' && (
                      <>
                        {sortHeader('totalHours', 'Összesen órák')}
                        {sortHeader('verifiedHours', 'Igazolt órák')}
                        {sortHeader('hourlyRate', 'Óradíj (Ft)')}
                        {sortHeader('totalEarnings', 'Keresett (Ft)')}
                        {sortHeader('totalCollected', 'Beszedett (Ft)')}
                        {sortHeader('amountDue', 'Fizetendő (Ft)')}
                        {sortHeader('trainingPaidOut', 'Kifizetett edzés (Ft)')}
                        {sortHeader('extraPaidOut', 'Egyéb kifiz. (Ft)')}
                        {sortHeader('balance', 'Hátralék (Ft)')}
                        {/*
                          A fejléc MEGNEVEZI a halmazt és kimondja, hogy KÍVÜL van a Fizetendőn
                          [2026-09-15] — enélkül a beszedett törölt-pénz csak egy magyarázatlan
                          szám lenne a kifizetési oszlopok mellett. Rendezés: a darabszám szerint.
                        */}
                        {sortHeader('deletedEvents', 'Utólag törölt (a Fizetendőn kívül)')}
                      </>
                    )}
                    {reportType === 'player' && (
                      <>
                        {sortHeader('paidEvents', 'Fizetett', {
                          info: RATE_EXPLANATIONS.playerPaidCount,
                          infoTestId: 'info-paid-count-header',
                        })}
                        {/* A bázis KIMONDVA: a jelenlétek közül — lásd a single kártya kommentjét. */}
                        {sortHeader('paymentRate', 'Fizetési arány (a jelenlétek közül)', {
                          info: RATE_EXPLANATIONS.playerPayment,
                          infoTestId: 'info-payment-rate-header',
                        })}
                        {sortHeader('totalPaymentAmount', 'Összes fizetett (Ft)')}
                        {/*
                          „UTÓLAG TÖRÖLT" OSZLOP [B-1, 2026-09-14] — a többjátékos sorhoz eddig
                          SEMMILYEN bontás nem tartozott, így a „Összes fizetett" oszlop néma
                          eltérést hordozott (törölt eseményen beszedett pénzt is tartalmaz).
                        */}
                        {sortHeader('deletedEvents', 'Utólag törölt')}
                      </>
                    )}
                    {sortHeader('lastActivity', 'Utolsó tevékenység')}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      {/*
                        A colSpan SZÁMOLT, nem fix 8 [javítva 2026-09-14]: az edzői nézetnek 14,
                        a játékosinak 9 oszlopa van, tehát a korábbi fix 8 MINDKETTŐNÉL hibás
                        volt (a betöltés-jelző nem a teljes táblát fedte).
                      */}
                      <TableCell colSpan={reportType === 'coach' ? 14 : 9} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : displayedMultiUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={reportType === 'coach' ? 14 : 9} align="center" data-testid="reports-filter-empty">
                        Nincs a keresésnek megfelelő név.
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedMultiUsers.map((user) => (
                      <TableRow key={user.userId}>
                        <TableCell sx={STICKY_NAME_CELL_SX}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                            {user.name}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={user.totalEvents}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={user.attendedEvents}
                            size="small"
                            color="success"
                          />
                        </TableCell>
                        {/*
                          A SZÁZALÉK-JELVÉNY SZÍNE: NINCS MIT MÉRNI = SEMLEGES [B-W3, 2026-09-16].

                          MI VOLT A BAJ: a szín kizárólag a százalékból jött, tehát akinek EGY
                          eseménye sem volt a vizsgált időszakban, „0%" jelvényt kapott VÖRÖSEN —
                          mintha elmaradt volna az edzésekről. Pedig nem hiányzott semmiről: nem
                          is volt mire eljönnie (új tag, szünet, más csoport). A klub ebből
                          hiányzónak bélyegzett tagokat, akiket soha nem is hívtak.

                          A JAVÍTÁS: ha a nevező 0 (`totalEvents`), a jelvény SEMLEGES, és a
                          szöveg is kimondja, hogy nincs adat — nem „0%" formájú vádirat.
                        */}
                        <TableCell align="center">
                          <Chip
                            label={user.totalEvents ? `${Math.round(user.participationRate || 0)}%` : '-'}
                            size="small"
                            color={
                              !user.totalEvents
                                ? 'default'
                                : (user.participationRate || 0) >= 75
                                  ? 'success'
                                  : (user.participationRate || 0) >= 50
                                    ? 'warning'
                                    : 'error'
                            }
                            title={user.totalEvents
                              ? undefined
                              : 'Ebben az időszakban nem volt esemény, amin részt vehetett volna — ezért nincs részvételi arány.'}
                          />
                        </TableCell>
                        {reportType === 'coach' && (
                          <>
                            <TableCell align="center">
                              <Chip
                                label={`${user.totalHours || 0} óra`}
                                size="small"
                                color="info"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={`${user.verifiedHours || 0} óra`}
                                size="small"
                                color="success"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={formatForint(user.hourlyRate)}
                                size="small"
                                color={user.hourlyRate ? 'primary' : 'default'}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={formatForint(user.totalEarnings)}
                                size="small"
                                color={user.totalEarnings ? 'info' : 'default'}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={formatForint(user.totalCollected)}
                                size="small"
                                color={user.totalCollected ? 'warning' : 'default'}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={formatForint(user.amountDue)}
                                size="small"
                                color={user.amountDue && user.amountDue > 0 ? 'success' : user.amountDue && user.amountDue < 0 ? 'error' : 'default'}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={formatForint(user.trainingPaidOut)}
                                size="small"
                                color={user.trainingPaidOut ? 'secondary' : 'default'}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={formatForint(user.extraPaidOut)}
                                size="small"
                                color={user.extraPaidOut ? 'info' : 'default'}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={formatForint(user.balance)}
                                size="small"
                                color={user.balance && user.balance > 0 ? 'error' : user.balance === 0 ? 'success' : 'default'}
                              />
                            </TableCell>
                            {/*
                              UTÓLAG TÖRÖLT ESEMÉNYEK PÉNZE, EDZŐNKÉNT [2026-09-15]. Ugyanaz a
                              szóhasználat, mint a játékos-táblázat „Utólag törölt" oszlopában.
                              Pénz nélküli törölt esemény esetén csak a darabszám látszik — enélkül
                              a sor azt sugallná, hogy nem is volt törölt edzése.
                            */}
                            <TableCell align="center" data-testid="coach-deleted-cell">
                              {(user.deletedEventsTotalCount ?? 0) > 0 ? (
                                <Chip
                                  label={
                                    (user.deletedEventsCollected ?? 0) > 0 || (user.deletedEventsPaidOut ?? 0) > 0
                                      ? `${user.deletedEventsTotalCount} db · beszedve ${formatForint(user.deletedEventsCollected ?? 0)} · kifizetve ${formatForint(user.deletedEventsPaidOut ?? 0)}`
                                      : `${user.deletedEventsTotalCount} db`
                                  }
                                  size="small"
                                  color="warning"
                                />
                              ) : (
                                <Chip label="-" size="small" color="default" />
                              )}
                            </TableCell>
                          </>
                        )}
                        {reportType === 'player' && (
                          <>
                            <TableCell align="center">
                              <Chip
                                label={user.paidEvents || 0}
                                size="small"
                                color="info"
                              />
                            </TableCell>
                            {/*
                              UGYANEZ A FIZETÉSI ARÁNYRA [B-W3, 2026-09-16; javítva a web-csomag
                              validátora után, 2026-09-16 14:00]: a „0%" VÖRÖSEN azt sugallta, hogy a
                              tag nem fizetett — holott nem is volt miért.
                              ⚠️ A NEVEZŐ ITT NEM A `totalEvents`, HANEM AZ `attendedEvents`: a szerver a
                              fizetési arányt a JELENLÉTEK közül számolja (`ReportsController.cs`
                              `PaymentRate = attendedEvents > 0 ? … / attendedEvents : 0`), és a fejléc is
                              ezt mondja („a jelenlétek közül"). Az első változat a `totalEvents`-re
                              kapuzott — MÉRVE: egy 5 eseményre beírt, de egyszer sem jelen lévő tag
                              vörös „0%"-ot kapott, pedig egyetlen alkalmon sem kellett fizetnie.
                              A részvételi arány nevezője viszont SZÁNDÉKOSAN a `totalEvents` (más kérdés).
                            */}
                            <TableCell align="center">
                              <Chip
                                label={user.attendedEvents ? `${Math.round(user.paymentRate || 0)}%` : '-'}
                                size="small"
                                color={
                                  !user.attendedEvents
                                    ? 'default'
                                    : (user.paymentRate || 0) >= 75
                                      ? 'success'
                                      : (user.paymentRate || 0) >= 50
                                        ? 'warning'
                                        : 'error'
                                }
                                title={user.attendedEvents
                                  ? undefined
                                  : 'Ebben az időszakban nem volt jelenléte, amiért fizetni kellett volna — ezért nincs fizetési arány.'}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={formatForint(user.totalPaymentAmount)}
                                size="small"
                                color={user.totalPaymentAmount ? 'success' : 'default'}
                              />
                            </TableCell>
                            {/*
                              PER-JÁTÉKOS TÖRÖLT-BONTÁS [B-1, 2026-09-14]: enélkül a sor
                              „Összes fizetett" értékét nem lehetett megmagyarázni, és a sor
                              részvétel-számai néma eltérést hordoztak.
                              ⚠️ A szó SZÁNDÉKOSAN „törölt", SOHA nem „lemondott" — az a felületen
                              a JÁTÉKOS leiratkozását jelenti (pótlási kredit,
                              `TrainingBalance.tsx:629`).
                            */}
                            <TableCell align="center" data-testid={`multi-deleted-cell-${user.userId}`}>
                              {(user.deletedEventsTotalCount ?? 0) > 0 ? (
                                <Chip
                                  label={
                                    (user.deletedEventsPaymentAmount ?? 0) > 0
                                      ? `${user.deletedEventsTotalCount} db · ${formatForint(user.deletedEventsPaymentAmount)}`
                                      : `${user.deletedEventsTotalCount} db`
                                  }
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  title={`${user.deletedEventsTotalCount} utólag törölt esemény jelenléti sora, ebből ${user.deletedEventsAttendedCount ?? 0} „jelen volt" jelöléssel — ezek nem számítanak részvételnek. Beszedve: ${formatForint(user.deletedEventsPaymentAmount ?? 0)} (${user.deletedEventsPaidCount ?? 0} esemény), ami az „Összes fizetett" RÉSZE.`}
                                />
                              ) : (
                                '-'
                              )}
                            </TableCell>
                          </>
                        )}
                        <TableCell align="center" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                          {user.lastActivity ? formatDate(user.lastActivity) : 'Nincs adat'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Venue Occupancy Report */}
        {venueOccupancyData && (
          <Paper sx={{ mb: 3 }}>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Helyszín kihasználtsági kimutatás ({venueOccupancyData.venueStatistics.length} helyszín)
              </Typography>
              <Button
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={exportReport}
              >
                CSV exportálás
              </Button>
            </Box>
            <Divider />

            {/* Summary Cards */}
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
                <Card sx={{ flex: 1, minWidth: 200 }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <VenueIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                    <Typography variant="h4" color="primary">
                      {venueOccupancyData.totalVenues}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Összes helyszín
                    </Typography>
                  </CardContent>
                </Card>

                <Card sx={{ flex: 1, minWidth: 200 }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <EventIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                    <Typography variant="h4" color="success.main">
                      {venueOccupancyData.totalEvents}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Összes esemény
                    </Typography>
                  </CardContent>
                </Card>

                <Card sx={{ flex: 1, minWidth: 200 }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <ScheduleIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                    <Typography variant="h4" color="warning.main">
                      {venueOccupancyData.overallOccupancyRate.toFixed(1)}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Átlagos kihasználtság
                    </Typography>
                  </CardContent>
                </Card>

                <Card sx={{ flex: 1, minWidth: 200 }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <CalendarIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                    <Typography variant="h4" color="info.main">
                      {venueOccupancyData.totalTables}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Összes asztal
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              {/* Top/Bottom Utilized Venues */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="h6" color="success.main" gutterBottom>
                      Leginkább használt helyszín
                    </Typography>
                    <Typography variant="h5">{venueOccupancyData.mostUtilizedVenue}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {venueOccupancyData.mostUtilizedRate.toFixed(1)}% kihasználtság
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="h6" color="warning.main" gutterBottom>
                      Legkevésbé használt helyszín
                    </Typography>
                    <Typography variant="h5">{venueOccupancyData.leastUtilizedVenue}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {venueOccupancyData.leastUtilizedRate.toFixed(1)}% kihasználtság
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              {/* Detailed Table */}
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Helyszín</TableCell>
                      <TableCell align="center">Asztalok száma</TableCell>
                      <TableCell align="center">Események</TableCell>
                      <TableCell align="center">Kihasználtság</TableCell>
                      <TableCell align="center">Csúcsóra</TableCell>
                      <TableCell align="center">Átlag asztal/esemény</TableCell>
                      <TableCell align="center">Asztal-órák</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : (
                      venueOccupancyData.venueStatistics.map((venue) => (
                        <TableRow key={venue.venueId}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <VenueIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                              {venue.venueName}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={venue.totalTables}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={venue.totalEvents}
                              size="small"
                              color="info"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={`${venue.occupancyRate.toFixed(1)}%`}
                              size="small"
                              color={venue.occupancyRate >= 75 ? 'error' : venue.occupancyRate >= 50 ? 'warning' : 'success'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {venue.peakHour !== null && venue.peakHour !== undefined ? (
                              <Chip
                                label={`${venue.peakHour}:00 (${venue.peakHourUsage} asztal)`}
                                size="small"
                                variant="outlined"
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Nincs adat
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">
                              {venue.averageTablesPerEvent.toFixed(1)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" color="text.secondary">
                              {venue.totalTableHours.toFixed(1)} / {venue.availableTableHours.toFixed(0)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>
        )}

        {/* Table Occupancy Report */}
        {tableOccupancyData && (
          <Paper sx={{ mb: 3 }}>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Asztal foglaltsági kimutatás - {tableOccupancyData.venueName}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<ExportIcon />}
                  onClick={exportReport}
                  size="small"
                >
                  CSV exportálás
                </Button>
              </Box>
            </Box>

            {/* Summary Cards */}
            <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Dátum
                  </Typography>
                  <Typography variant="h6">
                    {tableOccupancyData.date}
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Összes asztal
                  </Typography>
                  <Typography variant="h6">
                    {tableOccupancyData.totalTables}
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Csúcs óra
                  </Typography>
                  <Typography variant="h6">
                    {tableOccupancyData.peakHour}:00 ({tableOccupancyData.peakOccupancy} asztal)
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ flex: 1, minWidth: 200 }}>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Átlagos foglaltság
                  </Typography>
                  <Typography variant="h6">
                    {tableOccupancyData.averageOccupancy.toFixed(1)}%
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Hourly Occupancy Table */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Óra</TableCell>
                    <TableCell>Foglalt asztalok</TableCell>
                    <TableCell>Szabad asztalok</TableCell>
                    <TableCell>Foglaltsági arány</TableCell>
                    <TableCell>Események</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableOccupancyData.hourlyOccupancy.map((hour) => (
                    <TableRow key={hour.hour}>
                      <TableCell>{hour.hour.toString().padStart(2, '0')}:00</TableCell>
                      <TableCell>{hour.occupiedTables}</TableCell>
                      <TableCell>{hour.availableTables}</TableCell>
                      <TableCell>{hour.occupancyRate.toFixed(1)}%</TableCell>
                      <TableCell>
                        {hour.events.length > 0 ? (
                          <Box>
                            {hour.events.map((event, index) => (
                              <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                                {event.eventName} ({event.startTime}-{event.endTime}, {event.requiredTables} asztal)
                              </Typography>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Empty state */}
        {!loading &&
         ((reportType === 'table' && !tableOccupancyData && selectedDate && selectedVenueId) ||
          ((reportType === 'venue' || (reportType === 'coach' && dateFilterMode === 'range')) && !venueOccupancyData && startDate && endDate) ||
          (reportType !== 'venue' && reportType !== 'table' && reportMode === 'single' && reportData.length === 0 && selectedUser) ||
          (reportType !== 'venue' && reportType !== 'table' && reportMode === 'multi' && multiUserReportData.length === 0 && selectedMonth)) && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <ReportIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Nincs találat
            </Typography>
            <Typography variant="body2" color="text.secondary">
              A megadott szűrési feltételekkel nem található
              {reportType === 'venue' ? ' adat' : (reportMode === 'single' ? ' esemény' : ' felhasználó')}.
            </Typography>
          </Paper>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default Reports;